import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, PackageSearch } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

// Estado del catálogo de productos y botón para volver a descargar el feed.
// El feed son ~48 MB, así que la sincronización es manual: se lanza cuando el
// catálogo cambia, no en cada arranque.
function CatalogoProductos() {
  const [estado, setEstado] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  const miles = (n) => Number(n || 0).toLocaleString('es-ES');

  const cargar = useCallback(async () => {
    try {
      setEstado(await apiFetch('/api/catalogo/estado'));
    } catch {
      setEstado(null);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  async function sincronizar() {
    setSincronizando(true);
    try {
      const nuevo = await apiFetch('/api/catalogo/sincronizar', { method: 'POST' });
      setEstado(nuevo);
      toast.success(
        `Catálogo actualizado: ${miles(nuevo.productos)} productos y ${miles(nuevo.variantes)} variantes`,
      );
    } catch (err) {
      toast.error(`No se pudo sincronizar: ${err.message}`);
    } finally {
      setSincronizando(false);
    }
  }

  const fecha = estado?.actualizadoEn
    ? new Date(estado.actualizadoEn).toLocaleString('es-ES', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1.5 border-b">
        Catálogo de productos
      </div>
      <p className="text-xs text-muted-foreground">
        Sincronización del catálogo completo de productos
      </p>

      <div className="flex items-center gap-3 rounded-md border bg-muted/40 p-3">
        <PackageSearch className="h-4 w-4 text-primary shrink-0" />
        <div className="min-w-0 flex-1 text-xs">
          {estado?.productos ? (
            <>
              <div className="font-medium text-foreground">
                {miles(estado.productos)} productos · {miles(estado.variantes)} variantes
              </div>
              <div className="text-muted-foreground">Actualizado el {fecha}</div>
            </>
          ) : (
            <div className="text-muted-foreground">
              Todavía no se ha descargado el catálogo. El buscador no encontrará nada hasta
              que lo sincronices.
            </div>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={sincronizar} disabled={sincronizando}>
          <RefreshCw className={sincronizando ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
          {sincronizando ? 'Sincronizando…' : 'Sincronizar'}
        </Button>
      </div>
    </div>
  );
}

export default function AjustesModal({ onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ajustes</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <CatalogoProductos />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
